import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { secondsToHms } from "../../utils";
import StartTrim from "../CleoPlayer/icons/startTrim.svg?react";
import EndTrim from "../CleoPlayer/icons/endTrim.svg?react";
import { Tooltip } from "../Tooltip/Tooltip";
import type { GeneralPlayerHandle, ImageReel } from "../CleoPlayer";

interface VideoRangeTrimmerProps {
  posterUrl?: string;
  duration: number;
  start: number;
  end: number;
  onChange: (start: number, end: number) => void;
  disabled?: boolean;
  playerRef?: RefObject<GeneralPlayerHandle>;
  currentTime?: number;
  isPaused?: boolean;
  imageReel?: ImageReel | null;
  /** Ranges already published to this destination for this media - shown as
   * shaded/off-limits on the track so the user can see what to avoid while
   * trimming a new segment. Purely visual; enforcement happens on submit. */
  blockedRanges?: Array<{ start: number; end: number }>;
  /** Extra seconds added on both sides of each blocked range when rendering
   * the overlay, to match the buffer enforced on submit. */
  blockedRangesBuffer?: number;
  minDuration?: number;
}

const spriteFrameStyle = (
  reel: ImageReel,
  frameIndex: number
): React.CSSProperties => {
  const perSheet = Math.max(1, reel.rc * reel.rr);
  const sheet = Math.floor(frameIndex / perSheet);
  const within = frameIndex % perSheet;
  const col = within % reel.rc;
  const row = Math.floor(within / reel.rc);
  const ext = reel.ext?.[0] ?? "jpg";
  return {
    backgroundImage: `url(${reel.url}${sheet}.${ext})`,
    backgroundSize: `${reel.rc * 100}% ${reel.rr * 100}%`,
    backgroundPosition:
      reel.rc > 1 || reel.rr > 1
        ? `${(col / (reel.rc - 1 || 1)) * 100}% ${(row / (reel.rr - 1 || 1)) * 100}%`
        : "0% 0%",
    backgroundRepeat: "no-repeat",
  };
};

const frameIndexForTime = (tc: number[], time: number): number => {
  let idx = 0;
  let best = Infinity;
  for (let i = 0; i < tc.length; i++) {
    const diff = Math.abs(tc[i] - time);
    if (diff < best) {
      best = diff;
      idx = i;
    }
  }
  return idx;
};
const usableTimecodes = (tc: number[], duration: number): number[] => {
  const usable = tc.filter((t) => t <= duration);
  return usable.length ? usable : tc.slice(0, 1);
};

const thumbsForWidth = (trackWidth: number, trackHeight: number): number => {
  const tileWidth = trackHeight * (16 / 9);
  if (!(trackWidth > 0) || !(tileWidth > 0)) return 1;
  return Math.max(1, Math.round(trackWidth / tileWidth));
};
const slotFrameIndices = (
  tc: number[],
  duration: number,
  count: number
): number[] =>
  Array.from({ length: count }, (_, i) =>
    frameIndexForTime(tc, ((i + 0.5) / count) * duration)
  );

const NICE_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600];

const rulerTicks = (duration: number, maxTicks = 8): number[] => {
  if (!Number.isFinite(duration) || duration <= 0) return [0];
  const step =
    NICE_STEPS.find((s) => duration / s <= maxTicks) ??
    NICE_STEPS[NICE_STEPS.length - 1];
  const ticks: number[] = [];
  for (let t = 0; t < duration; t += step) ticks.push(t);
  const last = ticks[ticks.length - 1];
  if (duration - last < step * 0.35) ticks.pop();
  ticks.push(duration);
  return ticks;
};

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const TRACK_HEIGHT = 58;
const FPS = 25;
const FRAME_STEP = 1 / FPS;

const pad2 = (n: number) => (n < 10 ? `0${n}` : `${n}`);

const formatTimecode = (seconds: number): string => {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const total = Math.floor(safe);
  const frames = Math.min(FPS - 1, Math.floor((safe - total) * FPS));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const hms =
    h > 0 ? `${pad2(h)}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
  return `${hms}:${pad2(frames)}`;
};

const parseTimecode = (value: string): number => {
  const parts = value.trim().split(/[:;]/);
  if (parts.length < 2 || parts.length > 4) return NaN;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return NaN;

  if (nums.length === 2) return nums[0] * 60 + nums[1];
  if (nums.length === 3)
    return nums[0] * 60 + nums[1] + Math.min(nums[2], FPS - 1) / FPS;
  return (
    nums[0] * 3600 + nums[1] * 60 + nums[2] + Math.min(nums[3], FPS - 1) / FPS
  );
};
const ACCENT = "#e8a33d";
const HANDLE_COLOR = "#4d9fff";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const VideoRangeTrimmer = ({
  posterUrl,
  duration,
  start,
  end,
  onChange,
  disabled = false,
  playerRef,
  currentTime = 0,
  isPaused = true,
  imageReel,
  blockedRanges,
  blockedRangesBuffer = 0,
  minDuration = 1,
}: VideoRangeTrimmerProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [drag, setDrag] = useState<null | {
    type: "start" | "end" | "playhead" | "segment";
    origStart: number;
    origEnd: number;
    grabTime?: number;
  }>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  const [startInput, setStartInput] = useState(formatTimecode(start));
  const [endInput, setEndInput] = useState(formatTimecode(end));
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [showSeekBadge, setShowSeekBadge] = useState(false);
  const seekBadgeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const flashSeekBadge = () => {
    setShowSeekBadge(true);
    if (seekBadgeTimeoutRef.current) clearTimeout(seekBadgeTimeoutRef.current);
    seekBadgeTimeoutRef.current = setTimeout(
      () => setShowSeekBadge(false),
      800
    );
  };

  useEffect(() => {
    return () => {
      if (seekBadgeTimeoutRef.current)
        clearTimeout(seekBadgeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      setTrackWidth(entries[0].contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const effectiveDuration = duration || 1;
  // A clip can never outlast the media, so media shorter than minDuration
  // falls back to its own length rather than locking the handles solid.
  const minSpan = Math.min(minDuration, effectiveDuration);

  const thumbCount = useMemo(
    () => thumbsForWidth(trackWidth * zoom, TRACK_HEIGHT),
    [trackWidth, zoom]
  );
  const slotFrames = useMemo(
    () =>
      imageReel?.url && imageReel.tc?.length
        ? slotFrameIndices(
            usableTimecodes(imageReel.tc, effectiveDuration),
            effectiveDuration,
            thumbCount
          )
        : null,
    [imageReel, effectiveDuration, thumbCount]
  );

  const timeToPercent = (time: number) => (time / effectiveDuration) * 100;

  useEffect(() => {
    setStartInput(formatTimecode(start));
  }, [start]);

  useEffect(() => {
    setEndInput(formatTimecode(end));
  }, [end]);

  const seekTo = (time: number) => {
    playerRef?.current?.seek(time);
  };

  const pauseThenSeek = (time: number) => {
    if (isPreviewing) setIsPreviewing(false);
    playerRef?.current?.pause();
    seekTo(time);
    flashSeekBadge();
  };

  const handlePlayPause = () => {
    if (disabled || !playerRef?.current) return;
    if (isPreviewing) setIsPreviewing(false);
    if (playerRef.current.isPaused()) {
      playerRef.current.play();
    } else {
      playerRef.current.pause();
    }
  };

  const handleSkip = (offset: number) => {
    if (disabled) return;
    pauseThenSeek(clamp(currentTime + offset, 0, effectiveDuration));
  };

  const canSkip = (offset: number) => Math.abs(offset) < effectiveDuration;

  useEffect(() => {
    if (disabled) return;

    const isTyping = (el: EventTarget | null) => {
      const node = el as HTMLElement | null;
      if (!node) return false;
      const tag = node.tagName;
      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        node.isContentEditable
      );
    };

    const handleKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      const coarse = e.ctrlKey || e.altKey;
      const step = coarse ? 10 : e.shiftKey ? 1 : FRAME_STEP;

      switch (e.key) {
        case "ArrowLeft":
          e.preventDefault();
          handleSkip(-step);
          break;
        case "ArrowRight":
          e.preventDefault();
          handleSkip(step);
          break;
        case "ArrowUp":
          e.preventDefault();
          pauseThenSeek(0);
          break;
        case "ArrowDown":
          e.preventDefault();
          pauseThenSeek(effectiveDuration);
          break;
        case " ":
          e.preventDefault();
          handlePlayPause();
          break;
        default:
      }
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [disabled, currentTime, effectiveDuration, isPaused, playerRef]);

  const handleSetIn = () => {
    if (disabled) return;
    pauseThenSeek(currentTime);
    const newStart = clamp(currentTime, 0, end - minSpan);
    onChange(newStart, end);
  };

  const handleSetOut = () => {
    if (disabled) return;
    pauseThenSeek(currentTime);
    const newEnd = clamp(currentTime, start + minSpan, effectiveDuration);
    onChange(start, newEnd);
  };

  const previewEndRef = useRef<number | null>(null);
  const previewVideoRef = useRef<HTMLVideoElement | null>(null);

  const handlePreview = () => {
    if (disabled || !playerRef?.current) return;
    previewVideoRef.current = document.querySelector("video");
    previewEndRef.current = end;
    seekTo(start);
    playerRef.current.play();
    setIsPreviewing(true);
  };

  useEffect(() => {
    if (!isPreviewing) {
      previewEndRef.current = null;
      return;
    }
    const video = previewVideoRef.current;
    const outPoint = previewEndRef.current;
    if (!video || outPoint === null) return;

    const check = () => {
      if (video.currentTime >= outPoint) {
        playerRef?.current?.pause();
        setIsPreviewing(false);
      }
    };
    video.addEventListener("timeupdate", check);
    return () => video.removeEventListener("timeupdate", check);
  }, [isPreviewing, playerRef]);

  const commitStartInput = () => {
    const parsed = parseTimecode(startInput);
    if (Number.isNaN(parsed)) {
      setStartInput(formatTimecode(start));
      return;
    }
    const newStart = clamp(parsed, 0, end - minSpan);
    onChange(newStart, end);
    setStartInput(formatTimecode(newStart));
  };

  const commitEndInput = () => {
    const parsed = parseTimecode(endInput);
    if (Number.isNaN(parsed)) {
      setEndInput(formatTimecode(end));
      return;
    }
    const newEnd = clamp(parsed, start + minSpan, effectiveDuration);
    onChange(start, newEnd);
    setEndInput(formatTimecode(newEnd));
  };

  useEffect(() => {
    if (!drag || !trackRef.current) return;

    const handleMove = (e: MouseEvent) => {
      const { left, width } = trackRef.current!.getBoundingClientRect();
      const x = clamp(e.clientX - left, 0, width);
      const time = (x / width) * effectiveDuration;

      if (drag.type === "start") {
        const newStart = clamp(time, 0, end - minSpan);
        onChange(newStart, end);
      } else if (drag.type === "end") {
        const newEnd = clamp(time, start + minSpan, effectiveDuration);
        onChange(start, newEnd);
      } else if (drag.type === "segment") {
        const span = drag.origEnd - drag.origStart;
        const offset = time - (drag.grabTime ?? time);
        const newStart = clamp(
          drag.origStart + offset,
          0,
          effectiveDuration - span
        );
        onChange(newStart, newStart + span);
      } else {
        pauseThenSeek(clamp(time, 0, effectiveDuration));
      }
    };
    const handleUp = () => setDrag(null);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [drag, start, end, effectiveDuration, onChange]);

  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || drag || !trackRef.current) return;
    const { left, width } = trackRef.current.getBoundingClientRect();
    const x = clamp(e.clientX - left, 0, width);
    const time = (x / width) * effectiveDuration;
    pauseThenSeek(clamp(time, 0, effectiveDuration));
  };

  const ticks = useMemo(
    () => rulerTicks(effectiveDuration, zoom > 2 ? 16 : 8),
    [effectiveDuration, zoom]
  );

  return (
    <div className="w-100 bg-white py-1">
      <div style={{ overflowX: zoom > MIN_ZOOM ? "auto" : "visible" }}>
        <div style={{ width: `${zoom * 100}%`, minWidth: "100%" }}>
          <div
            style={{
              position: "relative",
              height: "16px",
              fontSize: "11px",
              color: "#333",
            }}
          >
            {ticks.map((t, i) => {
              const isFirst = i === 0;
              const isLast = i === ticks.length - 1;
              return (
                <span
                  key={t}
                  style={{
                    position: "absolute",
                    left: `${timeToPercent(t)}%`,
                    transform: isFirst
                      ? "none"
                      : isLast
                        ? "translateX(-100%)"
                        : "translateX(-50%)",
                    whiteSpace: "nowrap",
                  }}
                >
                  {secondsToHms(t)}
                </span>
              );
            })}
          </div>

          <div
            ref={trackRef}
            onClick={handleTrackClick}
            style={{
              position: "relative",
              height: `${TRACK_HEIGHT}px`,
              borderRadius: "8px",
              overflow: "visible",
              background: "#161327",
              cursor: disabled ? "default" : "pointer",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              {Array.from({ length: thumbCount }, (_, i) => {
                const from = (i / thumbCount) * effectiveDuration;
                const to = ((i + 1) / thumbCount) * effectiveDuration;
                const reelStyle =
                  imageReel && slotFrames
                    ? spriteFrameStyle(imageReel, slotFrames[i])
                    : null;
                return (
                  <div
                    key={i}
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${timeToPercent(from)}%`,
                      width: `${timeToPercent(to - from)}%`,
                      backgroundColor: i % 2 === 0 ? "#1c1830" : "#161327",
                      borderRight: "1px solid rgba(0,0,0,0.3)",
                      ...(reelStyle ?? {
                        backgroundImage: posterUrl
                          ? `url(${posterUrl})`
                          : undefined,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                      }),
                    }}
                  />
                );
              })}
            </div>

            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: 0,
                width: `${timeToPercent(start)}%`,
                background: "rgba(0,0,0,0.55)",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                right: 0,
                left: `${timeToPercent(end)}%`,
                background: "rgba(0,0,0,0.55)",
              }}
            />

            {blockedRanges?.flatMap((range, i) => {
              // Only the ±buffer zone around each boundary point is off-limits
              // (a near-duplicate republish), not the whole published span -
              // matches segmentOverlapsHistory's per-boundary check.
              const zones = [
                { key: `${i}-start`, point: range.start },
                { key: `${i}-end`, point: range.end },
              ];
              return zones.map(({ key, point }) => {
                const zoneStart = clamp(
                  point - blockedRangesBuffer,
                  0,
                  effectiveDuration
                );
                const zoneEnd = clamp(
                  point + blockedRangesBuffer,
                  0,
                  effectiveDuration
                );
                if (zoneEnd <= zoneStart) return null;
                return (
                  <div
                    key={key}
                    title="Already published to this channel"
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: `${timeToPercent(zoneStart)}%`,
                      width: `${timeToPercent(zoneEnd - zoneStart)}%`,
                      background:
                        "repeating-linear-gradient(45deg, rgba(220,53,69,0.35), rgba(220,53,69,0.35) 6px, rgba(220,53,69,0.55) 6px, rgba(220,53,69,0.55) 12px)",
                      pointerEvents: "none",
                      zIndex: 2,
                    }}
                  />
                );
              });
            })}

            <div
              onMouseDown={(e) => {
                if (disabled || !trackRef.current) return;
                e.stopPropagation();
                const { left, width } =
                  trackRef.current.getBoundingClientRect();
                const grabTime =
                  (clamp(e.clientX - left, 0, width) / width) *
                  effectiveDuration;
                setDrag({
                  type: "segment",
                  origStart: start,
                  origEnd: end,
                  grabTime,
                });
              }}
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                left: `${timeToPercent(start)}%`,
                width: `${timeToPercent(end) - timeToPercent(start)}%`,
                border: `2px solid ${HANDLE_COLOR}`,
                boxSizing: "border-box",
                zIndex: 1,
                cursor: disabled ? "default" : "grab",
              }}
            />

            {renderHandle(
              timeToPercent(start),
              disabled,
              () => setDrag({ type: "start", origStart: start, origEnd: end })
            )}

            {renderHandle(
              timeToPercent(end),
              disabled,
              () => setDrag({ type: "end", origStart: start, origEnd: end })
            )}

            <div
              onMouseDown={(e) => {
                if (disabled) return;
                e.stopPropagation();
                setDrag({ type: "playhead", origStart: start, origEnd: end });
              }}
              style={{
                position: "absolute",
                top: "-6px",
                bottom: "-6px",
                left: `${timeToPercent(clamp(currentTime, 0, effectiveDuration))}%`,
                width: "4px",
                marginLeft: "-2px",
                background: "#fff",
                borderRadius: "2px",
                boxShadow: "rgba(0, 0, 0, 0.6) 0px 0px 0px 1px, rgba(0, 0, 0, 0.4) 0px 0px 4px",
                cursor: disabled ? "default" : "ew-resize",
                pointerEvents: disabled ? "none" : "auto",
                zIndex: 4,
              }}
            >
              {(showSeekBadge || drag?.type === "playhead") && (
                <span
                  style={{
                    position: "absolute",
                    top: "-20px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    whiteSpace: "nowrap",
                    fontSize: "10px",
                    fontVariantNumeric: "tabular-nums",
                    color: "#fff",
                    background: "rgba(0,0,0,0.75)",
                    padding: "1px 5px",
                    borderRadius: "4px",
                    pointerEvents: "none",
                  }}
                >
                  {formatTimecode(currentTime)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className="d-flex align-items-center justify-content-end H"
        style={{ gap: "8px", marginTop: "10px" }}
      >
        <i className="bi bi-zoom-out" style={{ color: "#adb5bd" }} />
        <input
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.1}
          value={zoom}
          disabled={disabled}
          onChange={(e) => setZoom(Number(e.target.value))}
          className="range-accent"
          style={{ width: "140px", accentColor: ACCENT }}
        />
        <i className="bi bi-zoom-in" style={{ color: "#adb5bd" }} />
      </div>
      
      <div
        className="d-flex align-items-center justify-content-center position-relative"
        style={{ gap: "16px", marginTop: "25px" }}
      >
        <div className="d-flex flex-row align-items-center" style={{ gap: "4px" }}>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "#888",
            }}
          >
            START
          </span>
          <input
            type="text"
            value={startInput}
            disabled={disabled}
            onChange={(e) => setStartInput(e.target.value)}
            onBlur={commitStartInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitStartInput();
            }}
            style={{
              width: "70px",
              fontSize: "11px",
              color: "#212529",
              background: "#f1f2f4",
              border: "none",
              borderRadius: "6px",
              padding: "6px 10px",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </div>

        <div
          className="d-flex align-items-center"
          style={{
            gap: "6px",
            background: "#f1f2f4",
            borderRadius: "999px",
            // padding: "8px 14px",
          }}
        >
          {renderRoundButton(
            "Back 20s",
            <i className="bi bi-skip-start-fill f-16" />,
            () => handleSkip(-20),
            disabled || !canSkip(20)
          )}
          {renderRoundButton(
            "Back 10s",
            <i className="bi bi-caret-left-fill f-16" />,
            () => handleSkip(-10),
            disabled || !canSkip(10)
          )}
          {renderRoundButton(
            "In",
            <StartTrim width={16} height={16} />,
            handleSetIn,
            disabled
          )}
          {renderRoundButton(
            isPaused ? "Play" : "Pause",
            <i
              className={
                isPaused ? "bi bi-play-fill f-16" : "bi bi-pause-fill f-16"
              }
              style={{ fontSize: "18px", color: "#fff" }}
            />,
            handlePlayPause,
            disabled,
            true
          )}
          {renderRoundButton(
            "Out",
            <EndTrim width={16} height={16} />,
            handleSetOut,
            disabled
          )}
          {renderRoundButton(
            "Forward 10s",
            <i className="bi bi-caret-right-fill f-16" />,
            () => handleSkip(10),
            disabled || !canSkip(10)
          )}
          {renderRoundButton(
            "Forward 20s",
            <i className="bi bi-skip-end-fill f-16" />,
            () => handleSkip(20),
            disabled || !canSkip(20)
          )}
        </div>

        <div className="d-flex flex-row align-items-center" style={{ gap: "4px" }}>
          <span
            style={{
              fontSize: "10px",
              fontWeight: 500,
              letterSpacing: "0.04em",
              color: "#999",
            }}
          >
            END
          </span>
          <input
            type="text"
            value={endInput}
            disabled={disabled}
            onChange={(e) => setEndInput(e.target.value)}
            onBlur={commitEndInput}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEndInput();
            }}
            style={{
              width: "70px",
              fontSize: "11px",
              color: "#212529",
              background: "#f1f2f4",
              border: "none",
              borderRadius: "6px",
              padding: "6px 10px",
              fontVariantNumeric: "tabular-nums",
            }}
          />
        </div>

        <button
          type="button"
          title="Preview segment"
          disabled={disabled}
          onClick={handlePreview}
          className="d-flex align-items-center preview-clip-btn"
          style={{
            gap: "6px",
            marginLeft: "8px",
            borderRadius: "999px",
            padding: "4px 10px",
            border: "1px solid #007aff",
            fontSize: "11px",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            position: "absolute",
            right: 0,
          }}
        >
          Preview Clip
        </button>
      </div>
      {minDuration > 1 && (
        <div
          style={{
            fontSize: "10px",
            color: "#888",
            textAlign: "center",
            marginTop: "6px",
          }}
        >
          {`* Clip duration must be a minimum of ${minDuration} seconds.`}
        </div>
      )}
    </div>
  );
};

const HANDLE_GRIP_STYLE: React.CSSProperties = {
  width: "2px",
  height: "18px",
  borderRadius: "1px",
  background: "rgba(255,255,255,0.85)",
};

const renderHandle = (
  leftPercent: number,
  disabled: boolean,
  onGrab: () => void
) => (
  <div
    onMouseDown={(e) => {
      if (disabled) return;
      e.stopPropagation();
      onGrab();
    }}
    style={{
      position: "absolute",
      top: 0,
      bottom: 0,
      left: `clamp(0px, calc(${leftPercent}% - 10px), calc(100% - 20px))`,
      width: "20px",
      background: HANDLE_COLOR,
      borderRadius: "8px",
      cursor: disabled ? "default" : "ew-resize",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "3px",
      zIndex: 3,
      boxShadow: "0 1px 4px rgba(0,0,0,0.35)",
    }}
  >
    <span style={HANDLE_GRIP_STYLE} />
    <span style={HANDLE_GRIP_STYLE} />
  </div>
);

const renderRoundButton = (
  title: string,
  icon: React.ReactNode,
  onClick: () => void,
  disabled: boolean,
  primary = false
) => (
  <Tooltip content={title} placement="top">
    <button
      type="button"
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: primary ? "40px" : "32px",
        height: primary ? "40px" : "32px",
        borderRadius: "50%",
        border: "none",
        background: primary ? "#007aff" : "transparent",
        color: primary ? "#fff" : "#495057",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {icon}
    </button>
  </Tooltip>
);

export default VideoRangeTrimmer;
